/* global globalThis */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import * as EGCartContext from "../../../../contexts/EGcartContext";
import CartItemCard, { fromPage } from "./CartItemCard";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import ApiServices from "../../../../services/ApiServices";
import {
  EG_CHECKOUT_CART,
  EG_ADD_TO_CART,
} from "../../../../constants/api-constants";
import { useLoading } from "../../../../hooks/useLoader";
import { useAuth } from "../../../../hooks/useAuth";
import "./cart.css";
import { Box, Paper } from "@mui/material";
import { entityType } from "../../../../constants/common";
import { SystemUsersEnum } from "../../../../constants/SystemUsers";
import { isCartAddInExistsInLocalStorage } from "../../../AddInsServices/ManageSubscriptiondetails";

const FREQUENCY_SORT_ORDER = {
  1: 1,
  2: 2,
  3: 3,
  4: 4,
  99: 5,
};

const FREQUENCY_LABELS = {
  1: "Monthly",
  2: "Quarterly",
  3: "Semi-Annual",
  4: "Annual",
  99: "One Time",
};

const EGCartPage = () => {
  const history = useHistory();
  const auth = useAuth();
  const { cart, cartMetadata, getCartDetails, count } =
    EGCartContext.useEGCart();
  const { showLoader } = useLoading();
  const [loading, setLoading] = useState(true);
  const [checkoutLoadingGroup, setCheckoutLoadingGroup] = useState(null);
  const [selectedSubscriptions, setSelectedSubscriptions] = useState({});

  const getItemSubscriptions = useCallback((item) => {
    return item.content?.subscriptions || item.addIn?.subscriptions || [];
  }, []);

  const getSubscriptionId = useCallback((subscription) => {
    return (
      subscription?.contentSubscriptionId ?? subscription?.addInSubscriptionId
    );
  }, []);

  useEffect(() => {
    const fetchCart = async () => {
      setLoading(true);
      showLoader(true);
      try {
        await getCartDetails();
      } catch (error) {
        console.error("Error fetching cart details:", error);
      } finally {
        showLoader(false);
        setLoading(false);
      }
    };
    fetchCart();
    // Run only on initial mount to avoid re-fetch loops from changing context refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const initialSelections = {};
    cart.forEach((item) => {
      const subscriptions = getItemSubscriptions(item);
      const currentSub = subscriptions.find(
        (sub) =>
          sub.frequency === item.frequency &&
          Boolean(sub.isRecurring) === Boolean(item.isRecurring) &&
          Number(sub.actualValue || 0) === Number(item.amount || 0),
      );
      initialSelections[item.cartItemId] = currentSub
        ? getSubscriptionId(currentSub)
        : getSubscriptionId(subscriptions[0]) || "free";
    });

    setSelectedSubscriptions((prev) => {
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(initialSelections);

      if (prevKeys.length !== nextKeys.length) {
        return initialSelections;
      }

      const hasChanges = nextKeys.some(
        (key) => prev[key] !== initialSelections[key],
      );

      return hasChanges ? initialSelections : prev;
    });
  }, [cart, getItemSubscriptions, getSubscriptionId]);

  // Reset checkout loading state when user returns to the page
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setCheckoutLoadingGroup(null);
      }
    };
    setCheckoutLoadingGroup(null);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const handleSubscriptionChange = useCallback(
    async (item, newSubscriptionId) => {
      showLoader(true);
      try {
        const subscriptions = getItemSubscriptions(item);
        const newSubscription = subscriptions.find(
          (sub) =>
            sub.contentSubscriptionId === newSubscriptionId ||
            sub.addInSubscriptionId === newSubscriptionId,
        );

        if (!newSubscription) {
          console.error("Selected subscription not found");
          return;
        }

        // Prepare payload for ADD_TO_CART API
        const payload = {
          itemId: item.itemId || item.content?.contentId,
          amount: newSubscription.actualValue,
          transactionType: item.transactionType || 5,
          entityType: item.entityType || entityType.ENTERPRISES_GROUP,
          frequency: newSubscription.frequency,
          isRecurring: newSubscription.isRecurring,
        };

        // Call ADD_TO_CART API to update the cart item
        const response = await ApiServices.httpPost(EG_ADD_TO_CART, payload);

        if (response.responseStatus?.isSuccess) {
          // Update local state
          setSelectedSubscriptions((prev) => ({
            ...prev,
            [item.cartItemId]: newSubscriptionId,
          }));

          // Refresh cart to get updated data
          await getCartDetails();
        } else {
          console.error(
            "Failed to update subscription:",
            response.responseStatus?.error,
          );
        }
      } catch (error) {
        console.error("Error updating subscription:", error);
      } finally {
        showLoader(false);
      }
    },
    [getCartDetails, getItemSubscriptions, showLoader],
  );

  const getReturnScreen = useCallback(
    (isAutoRenewal, groupItems = []) => {
      if (isAutoRenewal) {
        return `type=renewal&enterpriseId=${cartMetadata?.userId}`;
      }

      if (!Array.isArray(groupItems) || groupItems.length === 0) {
        return "";
      }

      // Find the most recently added item in the group
      const newestItem = [...groupItems].sort((a, b) => {
        const aTime = a?.createdOn ? new Date(a.createdOn).getTime() : 0;
        const bTime = b?.createdOn ? new Date(b.createdOn).getTime() : 0;
        if (bTime !== aTime) return bTime - aTime;
        return (b?.cartItemId ?? 0) - (a?.cartItemId ?? 0);
      })[0];

      if (newestItem?.entityType === entityType.AD) {
        // sent type and itemId for ad payments to identify in payment processing and redirect to ad payment success page of ad
        return `type=adpay&itemId=${newestItem?.itemId || 0}`;
      }

      return "";
    },
    [cartMetadata?.userId],
  );

  const handleCheckout = useCallback(
    async (frequency, isRecurring, isAutoRenewal, groupItems = []) => {
      const groupKey = `${frequency}_${isRecurring}`;
      showLoader(true);
      try {
        setCheckoutLoadingGroup(groupKey);

        const return_screen = getReturnScreen(isAutoRenewal, groupItems);

        // Get the base URL for return
        const baseUrl = globalThis.location.origin;

        // Unique entity types from the items being checked out;
        // fallback to ENTERPRISES_GROUP if none are available.
        const uniqueEntityTypes = Array.from(
          new Set(
            (groupItems ?? [])
              .map((item) => item?.entityType)
              .filter((et) => et !== undefined && et !== null),
          ),
        );
        const entityTypesPayload =
          uniqueEntityTypes.length > 0
            ? uniqueEntityTypes
            : [entityType.ENTERPRISES_GROUP];

        const returnScreenSuffix = return_screen ? `&${return_screen}` : "";
        const successUrl = `${baseUrl}/eg-payment-process?session_id={CHECKOUT_SESSION_ID}${returnScreenSuffix}`;
        const payload = {
          expectedCartRowVersion: cartMetadata.rowVersion,
          isRecurring: isRecurring,
          frequency: frequency,
          successUrl: successUrl,
          cancelUrl: `${baseUrl}/cart/checkout`,
          entityTypes: entityTypesPayload,
          return_screen: return_screen,
        };

        const response = await ApiServices.httpPost(EG_CHECKOUT_CART, payload);

        if (response.responseStatus?.isSuccess) {
          const result = response.result;
          if (result?.isSuccessful && result?.stripeCheckoutUrl === "") {
            history.push("/eg-payment-success");
            // Keep loading state - navigating away
          } else if (result?.stripeCheckoutUrl) {
            globalThis.location.href = result.stripeCheckoutUrl;
            // Keep loading state - navigating away
          } else {
            console.error("Unexpected response format:", result);
            setCheckoutLoadingGroup(null); // Only reset on error
          }
        } else {
          console.error("Checkout failed:", response.responseStatus?.error);
          setCheckoutLoadingGroup(null); // Only reset on error
        }
      } catch (error) {
        console.error("Error during checkout:", error);
        setCheckoutLoadingGroup(null); // Only reset on error
      } finally {
        showLoader(false);
      }
    },
    [cartMetadata.rowVersion, getReturnScreen, history, showLoader],
  );

  const handleContinueShopping = useCallback(() => {
    const currentUser = auth?.getCurrentUser?.() || {};
    const loggedUserId = currentUser?.userId;
    const roleId = Number(auth?.getRole?.() || 0);

    if (!loggedUserId) {
      history.push("/dashboard");
      return;
    }

    if (roleId === SystemUsersEnum.DISTRIBUTOR) {
      history.push(`/distributor/${loggedUserId}/ad-management`);
      return;
    }

    if (roleId === SystemUsersEnum.ENTERPRISE) {
      history.push(`/enterprisegroup/${loggedUserId}/add-ins`);
      return;
    }

    history.push("/dashboard");
  }, [auth, history]);

  const handleBackFromCheckout = () => {
    if (window.history.length > 1) {
      history.goBack();
    } else {
      history.push("/");
    }
  };

  const determineFrequencyGroup = useCallback(
    (item, selectedSubId) => {
      const subscriptions = getItemSubscriptions(item);
      const ONE_TIME_FREQUENCY = 99;

      const isFreeOrZeroCost = (subscription, itemData) => {
        const costType = Number(subscription?.costType ?? 0);
        const actualValue = Number(
          subscription?.actualValue ?? itemData?.amount ?? 0,
        );

        // costType === 1 means always free
        if (costType === 1) return true;

        // costType === 2 with zero amount means free
        if (costType === 2 && actualValue === 0) return true;

        // Recurring subscription with zero value should be treated as one-time free
        if (actualValue === 0 && subscription?.frequency < ONE_TIME_FREQUENCY)
          return true;

        return false;
      };

      const toGroup = (subscription) => {
        // If it's free or zero cost, group as one-time free
        if (isFreeOrZeroCost(subscription, item)) {
          return {
            frequency: ONE_TIME_FREQUENCY,
            isRecurring: false,
          };
        }

        let subscriptionFrequency = Number(
          subscription?.frequency ?? ONE_TIME_FREQUENCY,
        );

        // If actual value is 0 for recurring, treat as one-time
        if (
          subscriptionFrequency < ONE_TIME_FREQUENCY &&
          Number(subscription?.actualValue ?? 0) === 0
        ) {
          subscriptionFrequency = ONE_TIME_FREQUENCY;
        }

        if (subscriptionFrequency === ONE_TIME_FREQUENCY) {
          return {
            frequency: ONE_TIME_FREQUENCY,
            isRecurring: false,
          };
        }

        return {
          frequency: subscriptionFrequency,
          isRecurring: Boolean(subscription?.isRecurring),
        };
      };

      // No subscriptions on item, fallback to cart-level frequency data.
      if (subscriptions.length === 0) {
        return {
          frequency: ONE_TIME_FREQUENCY,
          isRecurring: false,
        };
      }

      const selectedSub = subscriptions.find(
        (sub) =>
          sub.contentSubscriptionId === selectedSubId ||
          sub.addInSubscriptionId === selectedSubId,
      );

      // No subscription selected - fallback to first subscription
      if (!selectedSub) {
        return toGroup(subscriptions[0]);
      }

      return toGroup(selectedSub);
    },
    [getItemSubscriptions],
  );

  const groupedCart = useMemo(() => {
    const groups = {};
    cart.forEach((item) => {
      const selectedSubId = selectedSubscriptions[item.cartItemId];
      const { frequency, isRecurring } = determineFrequencyGroup(
        item,
        selectedSubId,
      );
      const groupKey = `${frequency}_${isRecurring}`;

      if (!groups[groupKey]) {
        groups[groupKey] = {
          frequency,
          isRecurring,
          items: [],
        };
      }
      groups[groupKey].items.push(item);
    });
    return groups;
  }, [cart, determineFrequencyGroup, selectedSubscriptions]);

  const getFrequencyLabel = useCallback((frequency) => {
    return FREQUENCY_LABELS[frequency] || "Unknown";
  }, []);

  const calculateGroupTotal = useCallback(
    (items) => {
      return items.reduce((sum, item) => {
        const subscriptions = getItemSubscriptions(item);
        const selectedSubId = selectedSubscriptions[item.cartItemId];
        const selected = subscriptions.find(
          (sub) =>
            sub.contentSubscriptionId === selectedSubId ||
            sub.addInSubscriptionId === selectedSubId,
        );
        return sum + (selected ? selected.actualValue : item.amount || 0);
      }, 0);
    },
    [getItemSubscriptions, selectedSubscriptions],
  );

  const sortedGroupedCart = useMemo(() => {
    return Object.entries(groupedCart).sort(([, leftGroup], [, rightGroup]) => {
      const leftFrequencyOrder =
        FREQUENCY_SORT_ORDER[leftGroup.frequency] || Number.MAX_SAFE_INTEGER;
      const rightFrequencyOrder =
        FREQUENCY_SORT_ORDER[rightGroup.frequency] || Number.MAX_SAFE_INTEGER;

      if (leftFrequencyOrder !== rightFrequencyOrder) {
        return leftFrequencyOrder - rightFrequencyOrder;
      }

      if (leftGroup.isRecurring !== rightGroup.isRecurring) {
        return leftGroup.isRecurring ? -1 : 1;
      }

      return 0;
    });
  }, [groupedCart]);

  const groupTotals = useMemo(() => {
    return sortedGroupedCart.reduce((acc, [groupKey, group]) => {
      acc[groupKey] = calculateGroupTotal(group.items);
      return acc;
    }, {});
  }, [calculateGroupTotal, sortedGroupedCart]);

  const hasMultipleFrequencies = sortedGroupedCart.length > 1;

  const renderCartContent = () => {
    if (loading) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <CircularProgress size={50} style={{ color: "white" }} />
          <div style={{ color: "white", fontSize: "16px" }}>
            Loading Cart Details..
          </div>
        </div>
      );
    }

    if (cart.length === 0) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: 80,
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 520,
              backgroundColor: "#f2f3f5",
              border: "1px solid #d9dde3",
              borderRadius: 12,
              padding: "28px 24px",
              textAlign: "center",
            }}
          >
            <ShoppingCartOutlinedIcon
              style={{ fontSize: "40px", color: "#5f6b7a", marginBottom: 8 }}
            />
            <div style={{ color: "#111", fontSize: "16px", marginBottom: 18 }}>
              No items in cart.
            </div>
            <button
              className="checkout-btn-group"
              onClick={handleContinueShopping}
              style={{ maxWidth: 260 }}
            >
              CONTINUE SHOPPING
            </button>
          </div>
        </div>
      );
    }

    return (
      <>
        {hasMultipleFrequencies && (
          <div className="cart-info-message eg-cart-info-message">
            <InfoOutlinedIcon style={{ fontSize: "20px", color: "#ff3b3b" }} />
            <div>
              <strong>
                Your cart contains items with different billing frequencies.
              </strong>
              <br />
              These items need to be purchased separately.
            </div>
          </div>
        )}

        {sortedGroupedCart.map(([groupKey, group]) => {
          const freq = group.frequency;
          const isRecurring = group.isRecurring;
          const items = group.items;
          const groupTotal = groupTotals[groupKey] || 0;
          const isAutoRenewal = isCartAddInExistsInLocalStorage(items);

          return (
            <Box
              key={groupKey}
              className="frequency-group"
              component={Paper}
              elevation={1}
            >
              <div className="frequency-header eg-frequency-header">
                <h3>{getFrequencyLabel(freq, isRecurring)} Subscriptions</h3>
              </div>

              <div className="frequency-items">
                {items.map((item) => (
                  <CartItemCard
                    key={item.cartItemId}
                    item={item}
                    selectedSubscription={
                      selectedSubscriptions[item.cartItemId] || "free"
                    }
                    onSubscriptionChange={handleSubscriptionChange}
                    fromPage={fromPage.ENTERPRISESGROUP_USER}
                    userId={cartMetadata?.userId}
                  />
                ))}
              </div>

              <div className="frequency-summary">
                <div className="summary-row">
                  <span>Total Items</span>
                  <span>{items.length}</span>
                </div>
                <div className="summary-row total">
                  <span>Subtotal</span>
                  <span>${groupTotal.toFixed(2)}</span>
                </div>
              </div>

              <div className="tax-notice eg-tax-notice">
                Taxes and additional charges will be calculated in the next step
                based on billing and shipping details.
              </div>

              <button
                className="checkout-btn-group"
                onClick={() =>
                  handleCheckout(freq, isRecurring, isAutoRenewal, items)
                }
                disabled={checkoutLoadingGroup !== null}
              >
                {checkoutLoadingGroup === groupKey ? (
                  <>
                    <CircularProgress
                      size={20}
                      style={{ color: "white", marginRight: "8px" }}
                    />
                    PROCESSING...
                  </>
                ) : (
                  <>
                    PROCEED TO CHECKOUT -{" "}
                    {getFrequencyLabel(freq, isRecurring).toUpperCase()}
                  </>
                )}
              </button>
            </Box>
          );
        })}
      </>
    );
  };

  return (
    <div className="eg-cart-wrapper frequency-group">
      {cart.length !== 0 && (
        <div className="cart-header">
          <button
            className="cart-header-icon eg-cart-header-icon"
            onClick={handleBackFromCheckout}
            data-testid="back-to-shopping-btn"
          >
            ← <span>Cart</span>
          </button>

          <div className="cart-header-icon disabled">
            <ShoppingCartOutlinedIcon sx={{ color: "#333" }} />{" "}
            {count > 0 && <span className="cart-badge">{count}</span>}
          </div>
        </div>
      )}

      {renderCartContent()}
    </div>
  );
};

export default EGCartPage;
